import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import { api } from '../api/client';
import { Prestation } from '../types';

/**
 * Référentiel des prestations — la liste plate des services réellement
 * délivrés (ateliers, cours, espaces, suivis). Unité stable de l'analyse :
 * les contrats achètent des paniers de prestations dont le contenu évolue,
 * les charges et les salaires s'y imputent. Consommé par Contrats (panier),
 * Charges (imputation) et RH (ventilation des fonctions de terrain).
 */
export function PrestationsPage() {
  const [prestations, setPrestations] = useState<Prestation[]>([]);
  const [erreur, setErreur] = useState<string | null>(null);
  const [chargement, setChargement] = useState(true);
  const [voirInactives, setVoirInactives] = useState(false);
  // null = fermé ; { prestation: null } = création ; { prestation } = édition
  const [modal, setModal] = useState<{ prestation: Prestation | null } | null>(null);

  const charger = useCallback(() => {
    setErreur(null);
    api.get<Prestation[]>('/prestations')
      .then(setPrestations)
      .catch((e) => setErreur(e.message))
      .finally(() => setChargement(false));
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const visibles = useMemo(
    () => prestations.filter((p) => voirInactives || p.actif),
    [prestations, voirInactives]
  );

  // Regroupement d'affichage, dans l'ordre d'apparition (l'ordre fait foi)
  const groupes = useMemo(() => {
    const parGroupe = new Map<string, Prestation[]>();
    for (const p of visibles) {
      const g = p.groupe ?? 'Sans groupe';
      if (!parGroupe.has(g)) parGroupe.set(g, []);
      parGroupe.get(g)!.push(p);
    }
    return [...parGroupe.entries()];
  }, [visibles]);

  /** Échange l'ordre avec la prestation voisine dans la liste complète. */
  async function deplacer(p: Prestation, sens: -1 | 1) {
    const liste = [...prestations].sort((a, b) => a.ordre - b.ordre || a.libelle.localeCompare(b.libelle));
    const i = liste.findIndex((x) => x.id === p.id);
    const voisin = liste[i + sens];
    if (!voisin) return;
    setErreur(null);
    try {
      const commentaire = 'Réordonnancement du référentiel';
      await api.put(`/prestations/${p.id}`, { ordre: voisin.ordre, commentaire });
      await api.put(`/prestations/${voisin.id}`, { ordre: p.ordre, commentaire });
      charger();
    } catch (e) {
      setErreur((e as Error).message);
    }
  }

  if (chargement) {
    return <div className="flex items-center justify-center" style={{ height: 240 }}><div className="spinner" /></div>;
  }

  return (
    <div>
      <div className="page-header">
        <div className="flex items-center gap-8">
          <h1 className="page-title">Prestations</h1>
          <span className="stat-pill">{prestations.filter((p) => p.actif).length} actives</span>
        </div>
        <div className="flex items-center gap-12">
          <label className="toggle-label">
            <span className="toggle">
              <input type="checkbox" className="toggle-input" checked={voirInactives}
                onChange={(e) => setVoirInactives(e.target.checked)} />
              <span className="toggle-slider" />
            </span>
            <span className="text-sm">Voir les archivées</span>
          </label>
          <button className="btn btn-accent" onClick={() => setModal({ prestation: null })}>Nouvelle prestation</button>
        </div>
      </div>

      {erreur && <div className="card mb-16 p-16 text-err">{erreur}</div>}

      <table className="table table-compact">
        <thead>
          <tr>
            <th>Prestation</th>
            <th>Description</th>
            <th>Clé</th>
            <th>Statut</th>
            <th style={{ width: 80 }} />
          </tr>
        </thead>
        <tbody>
          {groupes.map(([groupe, lignes]) => [
            <tr key={`g-${groupe}`} className="ligne-groupe">
              <td colSpan={5}>{groupe}</td>
            </tr>,
            ...lignes.map((p) => (
              <tr key={p.id} style={{ cursor: 'pointer', opacity: p.actif ? 1 : 0.55 }}
                onClick={() => setModal({ prestation: p })}>
                <td className="font-semi">{p.libelle}</td>
                <td className="text-sm text-ink-3">{p.description ?? '—'}</td>
                <td className="text-xs text-ink-3">{p.cle}</td>
                <td>{p.actif
                  ? <span className="badge badge-ok badge-sm">active</span>
                  : <span className="badge badge-sm">archivée</span>}</td>
                <td onClick={(e) => e.stopPropagation()}>
                  <div className="flex gap-4 justify-end">
                    <button className="btn btn-icon" title="Monter" onClick={() => deplacer(p, -1)}>
                      <ChevronUp className="icon-sm" />
                    </button>
                    <button className="btn btn-icon" title="Descendre" onClick={() => deplacer(p, 1)}>
                      <ChevronDown className="icon-sm" />
                    </button>
                  </div>
                </td>
              </tr>
            )),
          ])}
          {visibles.length === 0 && (
            <tr><td colSpan={5} className="text-ink-3">Aucune prestation.</td></tr>
          )}
        </tbody>
      </table>

      <p className="text-xs text-ink-3 mt-8">
        La prestation est l'unité stable : ce que recouvre une « mesure » achetée par un mandant change au fil
        des ans, la prestation non. Chaque ligne de contrat déclare le panier de prestations qu'elle recouvre
        (page Contrats) ; charges et salaires s'y imputent, d'où le coût réel par prestation.
      </p>

      {modal && (
        <PrestationModal
          prestation={modal.prestation}
          groupesExistants={[...new Set(prestations.map((p) => p.groupe).filter(Boolean) as string[])]}
          onFermer={() => setModal(null)}
          onModifie={() => { setModal(null); charger(); }}
        />
      )}
    </div>
  );
}

function PrestationModal({ prestation, groupesExistants, onFermer, onModifie }: {
  prestation: Prestation | null; // null = création
  groupesExistants: string[];
  onFermer: () => void;
  onModifie: () => void;
}) {
  const [libelle, setLibelle] = useState(prestation?.libelle ?? '');
  const [groupe, setGroupe] = useState(prestation?.groupe ?? '');
  const [description, setDescription] = useState(prestation?.description ?? '');
  const [actif, setActif] = useState(prestation?.actif ?? true);
  const [commentaire, setCommentaire] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onFermer(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onFermer]);

  const valide = libelle.trim().length > 0 && commentaire.trim().length > 0;

  async function enregistrer() {
    if (!valide || enCours) return;
    setEnCours(true);
    setErreur(null);
    const corps = {
      libelle: libelle.trim(),
      groupe: groupe.trim() || null,
      description: description.trim() || null,
      commentaire: commentaire.trim(),
    };
    try {
      if (prestation) await api.put(`/prestations/${prestation.id}`, { ...corps, actif });
      else await api.post('/prestations', corps);
      onModifie();
    } catch (e) {
      setErreur((e as Error).message);
      setEnCours(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onFermer}>
      <div className="modal" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">{prestation ? prestation.libelle : 'Nouvelle prestation'}</div>
          <button className="close-btn" onClick={onFermer} title="Fermer (Esc)"><X className="icon-sm" /></button>
        </div>
        <div className="modal-body" style={{ overflowY: 'auto' }}>
          <div className="flex flex-col gap-12">
            <div className="form-group">
              <label className="form-label">Libellé</label>
              <input className="form-input w-full" value={libelle} onChange={(e) => setLibelle(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Groupe <span className="text-ink-3">(regroupement d'affichage)</span></label>
              <input className="form-input w-full" list="groupes-prestation" value={groupe}
                onChange={(e) => setGroupe(e.target.value)} placeholder="Ateliers, Cours, Espaces…" />
              <datalist id="groupes-prestation">
                {groupesExistants.map((g) => <option key={g} value={g} />)}
              </datalist>
            </div>
            <div className="form-group">
              <label className="form-label">Description</label>
              <input className="form-input w-full" value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
            {prestation && (
              <label className="toggle-label">
                <span className="toggle">
                  <input type="checkbox" className="toggle-input" checked={actif} onChange={(e) => setActif(e.target.checked)} />
                  <span className="toggle-slider" />
                </span>
                <span className="text-sm">
                  Prestation active <span className="text-ink-3">— archiver conserve l'historique des imputations</span>
                </span>
              </label>
            )}
            <div className="form-group">
              <label className="form-label">Commentaire de modification (obligatoire)</label>
              <input className="form-input w-full" placeholder="Motif de la création ou de la modification…"
                value={commentaire} onChange={(e) => setCommentaire(e.target.value)} />
            </div>
            {erreur && <div className="text-err text-sm">{erreur}</div>}
            <div className="flex justify-end gap-8">
              <button className="btn" onClick={onFermer}>Annuler</button>
              <button className="btn btn-accent" disabled={!valide || enCours} onClick={enregistrer}>
                {enCours ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
